import { DriverDeletionImpactItem, TruckDeletionImpactItem } from 'src/entities/deletionImpact.entity';

export type DeletionConfirmResult = { blockedRunningNames: string[]; message: string; okLabel: string };

// 対象ごとの主語。1件なら「このトラック/このドライバー」、複数なら対象名（車番/氏名）で始める。
const subjectOf = (total: number, name: string, singular: string) => (total === 1 ? singular : name);

export const buildTruckDeletionConfirm = (items: TruckDeletionImpactItem[]): DeletionConfirmResult => {
  const blockedRunningNames = items.filter((it) => it.in_progress_count > 0).map((it) => it.license_plate_value);

  // 今後の運行予定を持つトラックだけ、対象ごとに1行で列挙する。
  const lines = items
    .filter((it) => it.future_shift_count > 0)
    .map((it) => `${subjectOf(items.length, it.license_plate_value, 'このトラック')}は、今後の運行予定で ${it.future_shift_count} 回使用されています。`);

  if (lines.length === 0) {
    const message = items.length === 1 ? 'このトラックを削除します。よろしいですか？' : `選択した ${items.length} 件を削除します。よろしいですか？`;
    return { blockedRunningNames, message, okLabel: '削除' };
  }

  const hasAllocated = items.some((it) => it.future_allocated_count > 0);
  // 同一トラック内でも未配車の予定(future_shift_count - future_allocated_count)が残れば
  // その shift は削除されるため、件差で「稼働のみ予定の有無」を判定する。
  const hasScheduleOnly = items.some((it) => it.future_shift_count > it.future_allocated_count);

  let consequence: string;
  if (hasAllocated && hasScheduleOnly) {
    // トラック削除は稼働(shift)を消し、配車ありは案件を未割当に戻す。混在時は両方を告知する。
    consequence = '削除すると、今後の運行予定は削除され、対象の案件は未割当になります。削除してよろしいですか？';
  } else if (hasAllocated) {
    consequence = '削除すると、対象の案件は未割当になります。削除してよろしいですか？';
  } else {
    consequence = '削除すると、今後の運行予定は削除されます。削除してよろしいですか？';
  }

  return {
    blockedRunningNames,
    message: `${lines.join('\n')}\n${consequence}`,
    okLabel: hasAllocated ? '未割当にして削除' : '削除',
  };
};

export const buildDriverDeletionConfirm = (items: DriverDeletionImpactItem[]): DeletionConfirmResult => {
  const blockedRunningNames = items.filter((it) => it.in_progress_count > 0).map((it) => it.name);

  // 通常ドライバー設定は配車の有無に関わらず解除するため、末尾に注記する。
  const defaultTruckNames = Array.from(new Set(
    items.flatMap((it) => it.default_driver_trucks.map((tk) => tk.license_plate_value)),
  ));
  const defaultNote = defaultTruckNames.length > 0
    ? `\nなお通常ドライバー設定は解除されます（対象トラック: ${defaultTruckNames.join('、')}）。`
    : '';

  // ドライバー削除は shift を消さず driver_id=nil にするだけなので、配車ありのときだけ配車未設定を告知する。
  const hasAllocated = items.some((it) => it.future_allocated_count > 0);
  if (hasAllocated) {
    const lines = items
      .filter((it) => it.future_shift_count > 0)
      .map((it) => `${subjectOf(items.length, it.name, 'このドライバー')}は、今後の運行予定 ${it.future_shift_count} 回に設定されています。`);
    return {
      blockedRunningNames,
      message: `${lines.join('\n')}\n削除すると、対象の配車はドライバー未設定になります。削除してよろしいですか？${defaultNote}`,
      okLabel: '未設定にして削除',
    };
  }

  const base = items.length === 1 ? 'このドライバーを削除します。よろしいですか？' : `選択した ${items.length} 件を削除します。よろしいですか？`;
  return { blockedRunningNames, message: `${base}${defaultNote}`, okLabel: '削除' };
};
